import type { ReactNode } from "react";
import { cn } from "../../app/components/primitives/utils";

// The one frame of every order and quote page that has a money summary (a total, what is owed, what to do about
// it): the CIS order page, the Tharstern-era order page and the quote page, signed in or by emailed link, and the
// staff view's mirrors of them (they render the same bodies). Keeping the layout here, not in each body, is what
// stops those pages drifting apart.
//
//   - Wide (lg): a top band of two columns, the page's header on the left and the summary card on the right, tops
//     aligned; then everything else (the tabs and each tab's content) at the page's full width beneath.
//   - Narrow: the header, then the content, stacked, with the summary as the slim bar fixed to the bottom.
//   - No summary (a cancelled order, a closed quote): one column, kept to a reading width.
// The summary is not sticky: it sits in the band and scrolls away with it, so the tabs below never share a row
// with it. The body decides what the panel and the bar are (it owns their state); the frame only places them.

export function SummaryPageFrame({
  hasSummary,
  isSummaryBesidePage,
  header,
  panel,
  bar,
  children,
}: {
  /** False where there is nothing to total (a cancelled order): no band, no bar, a narrow single column. */
  hasSummary: boolean;
  /** Whether the viewport has room for the summary beside the header (the `lg` breakpoint). */
  isSummaryBesidePage: boolean;
  /** The page's header and anything a shell sets with it (back link, banners, staff actions). */
  header: ReactNode;
  /** The summary card, shown in the band when there is room. */
  panel: ReactNode;
  /** The slim bar and its sheet, shown instead of the panel when there is not. */
  bar: ReactNode;
  /** The tabs and their content, at full width. */
  children: ReactNode;
}) {
  const isSummaryInBand = hasSummary && isSummaryBesidePage;
  return (
    <div
      className={cn(hasSummary ? !isSummaryBesidePage && "pb-28" : "max-w-4xl")}
    >
      <div className="space-y-5">
        <div
          data-testid="page-top-band"
          className={cn(
            isSummaryInBand &&
              "grid grid-cols-[minmax(0,1fr)_20rem] items-start gap-8",
          )}
        >
          <div className="min-w-0 space-y-5">{header}</div>
          {isSummaryInBand && panel}
        </div>
        <div className="min-w-0 space-y-5">{children}</div>
      </div>
      {hasSummary && !isSummaryBesidePage && bar}
    </div>
  );
}
